import React, {
    useState,
    useImperativeHandle,
    forwardRef,
    useEffect,
    useMemo,
} from 'react';
import s from './s.module.less';
import { Button, Descriptions, Form, Input, Select } from 'antd';
import type { TAssessmentItem } from 'types/operation';
import commonS from 'styles/common.module.less';
import useLoadBasicData from 'hooks/useLoadBasicData';
import { isDevEnv, isTestEnv, isProductionEnv, isPrereleaseEnv } from 'utils/common';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import { Meta } from 'types/common';

type IProps = {
    item: TAssessmentItem;
    onSuccess: (obj: Partial<TAssessmentItem>) => void;
};

export interface IntroCardA {
    validateAndSubmit: () => boolean;
}

const toSingleValue = (raw: unknown): string => {
    if (Array.isArray(raw)) {
        return raw.length > 0 ? String(raw[0]) : '';
    }
    if (typeof raw === 'string' && raw) {
        return raw;
    }
    return '';
};

const IntroCard = forwardRef<IntroCardA, IProps>((props, ref) => {
    const { item, onSuccess } = props;
    const [isEdit, setIsEdit] = useState(false);
    const [form] = Form.useForm<TAssessmentItem & { active: boolean }>();
    const [loadBaiscData] = useLoadBasicData();
    const [setting] = useSettings();

    const helloKlaritySpecialtyOptions = useMemo(
        () => (setting?.[EnumFields.HELLO_KLARITY_SPECIALTY] || []) as Meta[],
        [setting],
    );
    const appointmentTypeOptions = useMemo(
        () => (setting?.[EnumFields.HELLO_KLARITY_SERVICE_TYPE] || []) as Meta[],
        [setting],
    );

    const handleEdit = () => {
        setIsEdit(true);
    };

    const handleCancel = () => {
        setIsEdit(false);
    };

    const handleSave = async () => {
        const formValues = await form.validateFields();
        onSuccess({
            assessmentName: formValues.assessmentName,
            partnerName: formValues.partnerName,
            specialty: (formValues as any).specialty || undefined,
            serviceType: (formValues as any).serviceType || undefined,
        } as any);
        setIsEdit(false);
    };

    const getLinkUrl = (): string => {
        if (!item?.uuid) {
            return '';
        }
        if (isDevEnv()) {
            return `https://dev.d2ysjydukc4efk.amplifyapp.com/awsome-embed?key=AK-test-api-key-1&uuid=${item.uuid}`;
        }
        if (isTestEnv()) {
            return `https://test.d2ysjydukc4efk.amplifyapp.com/awsome-embed?key=AK-test-api-key-1&uuid=${item.uuid}`;
        }
        if (isProductionEnv() || isPrereleaseEnv()) {
            return `https://www.helloklarity.com/awsome-embed?key=sk_prod_mh5dfc2mKXpI&uuid=${item.uuid}`;
        }
        return '';
    };

    const specialtyLabel = useMemo(() => {
        const value = toSingleValue((item as any)?.specialty);
        if (!value) return '';
        return helloKlaritySpecialtyOptions.find((o) => String(o.label) === value)?.label || value;
    }, [item, helloKlaritySpecialtyOptions]);

    const serviceTypeLabel = useMemo(() => {
        const value = toSingleValue((item as any)?.serviceType);
        if (!value) return '';
        return appointmentTypeOptions.find((o) => String(o.label) === value)?.label || value;
    }, [item, appointmentTypeOptions]);

    const renderDescription = () => {
        return (
            <Descriptions
                colon={false}
                title="Workflow information"
                layout="vertical"
                extra={<Button onClick={() => handleEdit()}>Edit</Button>}
                column={1}
                size="small"
                contentStyle={{
                    marginBottom: '16px',
                }}
            >
                <Descriptions.Item label="Workflow name">
                    {item.assessmentName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Specialty">
                    {specialtyLabel || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Appointment type">
                    {serviceTypeLabel || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Link">
                    {getLinkUrl() ? (
                        <a
                            href={getLinkUrl()}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                marginTop: 4,
                            }}
                        >
                            {getLinkUrl()}
                        </a>
                    ) : (
                        '-'
                    )}
                </Descriptions.Item>
            </Descriptions>
        );
    };

    const renderForm = () => {
        return (
            <div>
                <div className={s.headerBar}>
                    <h4 className={s.subtitle}>Workflow information</h4>
                    <div className={s.buttonWrap}>
                        <Button onClick={() => handleCancel()}>Cancel</Button>
                        <Button
                            onClick={() => handleSave()}
                            type="primary"
                            className={s.ml8}
                        >
                            Save changes
                        </Button>
                    </div>
                </div>
                <p className={s.tips}>
                    <span>* </span>
                    Indicates required fields
                </p>
                <Form
                    form={form}
                    className={commonS.formStyle1}
                    initialValues={{ active: true }}
                    autoComplete="off"
                    layout="vertical"
                    scrollToFirstError
                >
                    <>
                        <Form.Item
                            label="Assessment name"
                            name="assessmentName"
                            validateFirst
                            rules={[
                                {
                                    required: true,
                                    message: 'Assessment name is required',
                                },
                            ]}
                        >
                            <Input
                                maxLength={70}
                                showCount
                                placeholder="Ex. ADHD self assessment"
                            />
                        </Form.Item>
                        <Form.Item label="Specialty" name="specialty">
                            <Select
                                showSearch
                                virtual={false}
                                placeholder="Select specialty"
                                optionFilterProp="children"
                                allowClear
                            >
                                {helloKlaritySpecialtyOptions.map((opt) => (
                                    <Select.Option key={String(opt.label)} value={String(opt.label)}>
                                        {opt.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item label="Appointment type" name="serviceType">
                            <Select
                                showSearch
                                virtual={false}
                                placeholder="Select appointment type"
                                optionFilterProp="children"
                                allowClear
                            >
                                {appointmentTypeOptions.map((opt) => (
                                    <Select.Option key={String(opt.label)} value={String(opt.label)}>
                                        {opt.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </>
                </Form>
            </div>
        );
    };

    useImperativeHandle(ref, () => ({
        validateAndSubmit: () => {
            return !isEdit;
        },
    }));

    useEffect(() => {
        if (item) {
            form.setFieldsValue({
                assessmentName: item.assessmentName,
                partnerName: item.partnerName,
                specialty: toSingleValue((item as any)?.specialty),
                serviceType: toSingleValue((item as any)?.serviceType),
            } as any);
        } else {
            form.resetFields();
        }
    }, [item, form]);

    useEffect(() => {
        loadBaiscData();
    }, [loadBaiscData]);

    return (
        <div className={s.wrap}>
            <div className={s.content}>
                {isEdit ? renderForm() : renderDescription()}
            </div>
        </div>
    );
});

export default IntroCard;
